/* eslint-disable angular/no-private-call */

import { accountServiceModule } from '../account-service/account-service';
import { appConfig } from '../../constants/app-config';
import { uiRouterStateModule } from '../../vendor/ui-router-module';

export const permissionsServiceModule = angular
  .module('Q.services.permissions-service', [
    uiRouterStateModule,
    accountServiceModule,
  ])

  .factory('permissionsService', function (accountService, $q) {
    const AUTHORIZE_KEYS = [
      'loggedIn',
      'expired',
      'role',
      'privilege',
      'staff',
      'feature',
      'featureLevels',
      'productType',
      'userProperty',
      'devOnly',
      'when',
      'systemTeam',
      'templateTeam',
      'staffContent',
      'or',
    ];

    return {
      canAuthorize,
      isLoggedIn,
      role,
      privilege,
      feature,
      authorize,
      authorizeAny,
      authorizeAsync,
      // special case which hard-checks the current user's
      isStaff() {
        const account = accountService.get();
        const user = account.user;
        return user && user.role === 'staff';
      },
    };

    /**
     * Returns false if permissions cannot be determined synchronously due to the app not being
     * fully loaded.
     * @returns {boolean}
     */
    function canAuthorize() {
      return !!accountService.isReady;
    }

    /**
     * Returns the authorization via a promise.
     *
     * Note this result is guaranteed to pass or fail as it will wait until the app is initialized.
     *
     * @param {object} properties
     * @returns {Promise} Promise resolved if the authorization succeeds, or rejected if it fails.
     */
    function authorizeAsync(properties) {
      return accountService.whenReady().then(() => {
        return authorize(properties) ? $q.when(true) : $q.reject(false);
      });
    }

    function _notPermitted(prop, value) {
      // only returns true if prop is a boolean and the opposite of value's truthiness
      return prop === !value;
    }

    /**
     * Determines if the current account is authorized
     * @param {boolean?} options.loggedIn Pass true or false to only allow logged in or not logged in users, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.expired Pass true or false to only allow expired or non-expired teams, respectively.  Boolean true or false is _required_!
     * @param {string?} options.role A string to validate the currently logged in user against a role.  If there is no logged in user, the authorization fails.
     * @param {string?} options.privilege A string to validate the currently logged in user against a privilege.  If there is no logged in user, the authorization fails.
     * @param {boolean?} options.staff A special role that can be used to filter out staff members when set to false.
     * @param {string?} options.feature A string to validate the currently logged in user against a feature flag.  If there is no logged in user, the authorization fails.
     * @param {string|Array<string>?} options.featureLevels specific feature levels for complex features
     * @param {string?} options.productType If the current user's team has the given product type
     * @param {object?} options.userProperty Pass in a set of key-value pairs to be matched _exactly_ against the user object
     * @param {boolean?} options.devOnly If truthy, only authorize when in the development environment.
     * @param {function?} options.when If provided, tests that the return of this function is truthy.
     * @param {boolean?} options.systemTeam Pass true or false to only allow system or non-system teams, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.templateTeam Pass true or false to only allow the template or other teams, respectively.  Boolean true or false is _required_!
     * @param {boolean?} options.staffContent Pass true or false to only allow the template/system/staff.  Boolean true is _required_!
     * @param {object?} options.or Provide a set of authorization keys, returns true if any of them are true
     * @returns {boolean} True if authorized, or false if unauthorized or authorization fails.
     */
    function authorize(options) {
      const {
        loggedIn = null,
        expired = null,
        role = null,
        privilege = null,
        staff = null,
        feature = null,
        featureLevels = null,
        productType = null,
        userProperty = null,
        devOnly = false,
        when = null,
        systemTeam = null,
        templateTeam = null,
        staffContent = null,
        or = null,
      } = options || {};

      _validateAuthorizationOptions(options);

      // We cannot authorize before accountService has logged in
      if (!canAuthorize()) return false;

      const account = accountService.get();
      const user = account.user;
      const team = user && user.team;

      if (_notPermitted(loggedIn, account.verified)) return false;

      // ignore expiration rules for staff or non-logged-in
      if (user && !user.hasRole('staff')) {
        if (_notPermitted(expired, !team || team.subscriptionExpired()))
          return false;
      }

      if (role && (!user || !user.hasRole(role))) return false;

      if (privilege && (!user || !user.hasPrivilege(privilege))) return false;

      if (staff !== null && (!user || user.hasRole('staff') !== staff))
        return false;

      if (feature && (!user || !user.hasFeature(feature, featureLevels)))
        return false;

      if (
        productType &&
        (!team || !team.productTypes || !team.productTypes[productType])
      )
        return false;

      if (
        userProperty &&
        (!user ||
          Object.keys(userProperty).some(
            (k) => _getProperty(userProperty, k) !== _getProperty(user, k),
          ))
      )
        return false;

      if (devOnly && !appConfig.devMode) return false;

      if (when && angular.isFunction(when)) return when(account, user);

      if (_notPermitted(systemTeam, team && team.isSystemTeam())) return false;
      if (_notPermitted(templateTeam, team && team.isTemplateTeam))
        return false;

      if (
        _notPermitted(
          staffContent,
          team &&
            user &&
            (team.isSystemTeam() ||
              team.isTemplateTeam ||
              user.hasRole('staff')),
        )
      )
        return false;

      if (or !== null && !authorizeAny(or)) return false;

      return true;
    }

    /**
     * returns true if any option is true via authorize
     * @param options
     */
    function authorizeAny(options) {
      return Object.keys(options).some((k) => authorize({ [k]: options[k] }));
    }

    function isLoggedIn() {
      return authorize({ loggedIn: true });
    }

    /**
     * Convenience method for permissionsService.authorize({ role: roleName });
     * @param {string} roleName
     * @returns {boolean}
     */
    function role(roleName) {
      return authorize({ role: roleName });
    }

    /**
     * Convenience method for permissionsService.authorize({ privilege: privilegeName });
     * @param {string} privilegeName
     * @return {boolean}
     */
    function privilege(privilegeName) {
      return authorize({ privilege: privilegeName });
    }

    /**
     * Convenience method for permissionsService.authorize({ feature: featureName });
     * @param {string} featureName
     * @returns {boolean}
     */
    function feature(featureName) {
      return authorize({ feature: featureName });
    }

    //// private functions

    function _getProperty(obj, k) {
      return angular.isFunction(obj[k]) ? obj[k]() : obj[k];
    }

    function _validateAuthorizationOptions(options) {
      if (
        AUTHORIZE_KEYS.every(
          (k) => options[k] === undefined || options[k] === null,
        )
      ) {
        throw new Error(
          'No Valid Permission Checks Requested: ' + JSON.stringify(options),
        );
      }
    }
  })

  .run(function ($rootScope, permissionsService, $state) {
    // Enable using permissions service within any scope via $root.permit(...)
    $rootScope.permitted = permissionsService.authorize;
    $rootScope.anyPermitted = permissionsService.authorizeAny;
    $rootScope.notPermitted = (arg) => !permissionsService.authorize(arg);
    $rootScope.isStaff = permissionsService.isStaff;

    // Handle
    $rootScope.$on(
      '$stateChangeStart',
      function (event, toState, toParams, fromState, fromParams, options) {
        if (toParams.$$finishAuthorize) {
          delete toParams.$$finishAuthorize;
          return;
        }

        if (event.defaultPrevented) return;

        // If there are permissions set then prevent default and attempt to authorize
        let permissions;
        if (toState.data && toState.data.permissions) {
          permissions = toState.data.permissions;
        }

        if (permissions) {
          if (permissionsService.canAuthorize()) {
            if (!permissionsService.authorize(permissions)) {
              event.preventDefault();
              handleUnauthorizedRedirect();
            } else {
              $rootScope.$broadcast(
                '$stateChangePermissionAccepted',
                toState,
                toParams,
              );
            }
          } else {
            event.preventDefault();

            permissionsService
              .authorizeAsync(permissions)
              .then(function () {
                $rootScope.$broadcast(
                  '$stateChangePermissionAccepted',
                  toState,
                  toParams,
                );
                toParams.$$finishAuthorize = true;
                $state.go(toState.name, toParams, options);
              })
              .catch(handleUnauthorizedRedirect);
          }
        }

        function handleUnauthorizedRedirect() {
          $rootScope.$broadcast(
            '$stateChangePermissionDenied',
            toState,
            toParams,
          );
          // If not authorized, redirect to wherever the route has defined, if defined at all
          const redirectTo = permissions.redirectTo;
          if (redirectTo) {
            $state.go(redirectTo, toParams, { location: 'replace' });
          }
        }
      },
    );
  });
